import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Briefcase, ArrowLeft, Bookmark, MapPin, DollarSign, Trash2, EyeOff } from 'lucide-react';
import { formatPayDisplay } from '../utils/payDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SavedJobs() {
  const { user } = useAuth();
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/saved-jobs`, { withCredentials: true })
      .then(r => setSaved(r.data.saved_jobs))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const removeSaved = async (jobId) => {
    try {
      await axios.post(`${API}/saved-jobs/${jobId}`, {}, { withCredentials: true });
      setSaved(prev => prev.filter(s => s.job_id !== jobId));
    } catch (err) { console.error(err); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-4"><ArrowLeft size={14} strokeWidth={1.5} /> Dashboard</Link>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-[#D97757] flex items-center justify-center">
            <Bookmark size={22} className="text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-3xl font-medium tracking-tight" style={{fontFamily:'Outfit'}}>Saved Jobs</h1>
            <p className="text-[#1C2A24]/60 text-sm">{saved.length} jobs bookmarked</p>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 bg-white rounded-2xl animate-pulse" />)}</div>
        ) : saved.length === 0 ? (
          <div className="bg-white rounded-3xl border border-[#E5E0D8] p-12 text-center" data-testid="no-saved">
            <Bookmark size={48} className="text-[#E5E0D8] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-lg text-[#1C2A24]/60 mb-2">No saved jobs yet</p>
            <p className="text-sm text-[#1C2A24]/40 mb-4">Click the bookmark icon on any job to save it here.</p>
            <Link to="/jobs"><Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid="browse-jobs-btn">Browse Jobs</Button></Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="saved-jobs-list">
            {saved.map(s => (
              <div key={s.id} className="bg-white rounded-2xl border border-[#E5E0D8] p-5 shadow-[0_4px_15px_rgb(0,0,0,0.03)] flex items-center gap-4" data-testid={`saved-${s.job_id}`}>
                <div className="w-10 h-10 rounded-xl bg-[#F4F1E8] flex items-center justify-center shrink-0">
                  <Briefcase size={18} className="text-[#047857]" strokeWidth={1.5} />
                </div>
                <Link to={`/jobs/${s.job_id}`} className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm truncate" style={{fontFamily:'Outfit'}}>{s.job_title}</h3>
                  <div className="flex items-center gap-3 text-xs text-[#1C2A24]/50 mt-1">
                    <span>{s.company}</span>
                    <span className="flex items-center gap-1"><MapPin size={10} />{s.state}</span>
                    <span className="flex items-center gap-1">{(() => { const p = formatPayDisplay(s.salary_range); return p.isDisclosed ? <><DollarSign size={10} />{p.text}</> : <><EyeOff size={10} />{p.text}</>; })()}</span>
                  </div>
                </Link>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#1C2A24]/30">{new Date(s.saved_at).toLocaleDateString()}</span>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-full" onClick={() => removeSaved(s.job_id)} data-testid={`unsave-${s.job_id}`}>
                    <Trash2 size={14} className="text-red-400" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
